/**
 * Word's line model as CSS custom properties (RED-317 PR 3c-D).
 *
 * Word does not multiply a paragraph's font size by its line spacing the way
 * CSS `line-height: 1.15` does. It lays every line out from the font's own
 * vertical metrics, measured in Word.app 16.112 (scratch probes, one font
 * and rule per page, char baselines read from Word's PDF export):
 *
 *   single line S = ascent + descent + lineGap        (Calibri 11 pt: 13.43 pt, not 11)
 *   auto  (line/240 = f)  height f × S, baseline at ascent + lineGap from the
 *                         line top (the extra space goes below the text);
 *                         below 1 the whole line scales: baseline at f × (ascent + lineGap)
 *   exact (line twips)    height = line, baseline at 0.8 × line, whatever the font
 *   atLeast               height max(line, S), the extra space above the text
 *
 * The lineGap Word adds is GDI's external leading: it sits above the ascent.
 * A line holding several fonts or sizes takes the largest part above and the
 * largest part below the baseline.
 *
 * CSS centres the leading instead (half above, half below the font's ascent
 * + descent), so the canvas sets the line height from these metrics and moves
 * each run's glyphs to Word's baseline with a relative offset (studio.css,
 * `.studio-run`). Fonts set the metric properties next to font-family
 * (fontMetricsCss); paragraphs set the rule (lineSpacingCss).
 */

/**
 * Per font, in font units: [unitsPerEm, ascent, descent, lineGap]. ascent and
 * descent are OS/2 usWinAscent / usWinDescent (equal to hhea's for every font
 * here, which is what browsers draw as the text's content area); lineGap is
 * GDI's external leading, max(0, hhea.lineGap − ((winAscent + winDescent) −
 * (hhea.ascender − hhea.descender))). Sources, each matching the line pitch
 * Word draws at 11 pt in the probes:
 *
 * - Calibri 6.20 / Calibri Light 6.20 (Word.app DFonts): 2048 1950 550 0 — 13.44 pt.
 *   Carlito (public/fonts) carries the same numbers.
 * - Cambria 6.90i (Word.app DFonts Cambria.ttc): 2048 1946 455 0 — 12.88 pt.
 *   Caladea's differ (1000 900 250 0, win 1050/250), so fonts.css overrides them.
 * - Times New Roman 5.01.4x (macOS Supplemental, the file Word.app embeds):
 *   2048 1825 443, hhea lineGap 87 → 87 — 12.64 pt. Tinos carries the same.
 * - Arial 5.01.2x (macOS Supplemental): 2048 1854 434, hhea lineGap 67 → 67 — 12.64 pt.
 *   Arimo's hhea/typo carry the same.
 * - Courier New 5.00.2x (macOS Supplemental): 2048 1705 615 0 — 12.48 pt. Cousine the same.
 * - Garamond 2.40 (Word.app DFonts): 2048 1765 539 0 — 12.32 pt.
 * - Tahoma 6.98 (Word.app DFonts): 2048 2049 423 0 — 13.28 pt.
 * - Segoe UI (hhea of the copy Word embeds in its PDF): 2048 2210 514 0 — 14.64 pt.
 *   Segoe UI Symbol (the ✔ of the Pärnu malls, embedded the same way) carries the same.
 */
const FONT_UNITS = {
  Calibri: [2048, 1950, 550, 0],
  Cambria: [2048, 1946, 455, 0],
  'Times New Roman': [2048, 1825, 443, 87],
  Arial: [2048, 1854, 434, 67],
  'Courier New': [2048, 1705, 615, 0],
  Garamond: [2048, 1765, 539, 0],
  Tahoma: [2048, 2049, 423, 0],
  'Segoe UI': [2048, 2210, 514, 0],
}

/** Names that draw with one of the fonts above (fontStack.js's metric-compatible aliases). */
const SAME_METRICS = {
  'Calibri Light': 'Calibri',
  'Segoe UI Symbol': 'Segoe UI',
  Carlito: 'Calibri',
  'Studio Calibri': 'Calibri',
  Caladea: 'Cambria',
  'Studio Cambria': 'Cambria',
  Tinos: 'Times New Roman',
  'Liberation Serif': 'Times New Roman',
  'Studio Times': 'Times New Roman',
  Arimo: 'Arial',
  'Liberation Sans': 'Arial',
  'Studio Arial': 'Arial',
  Courier: 'Courier New',
  Cousine: 'Courier New',
  'Liberation Mono': 'Courier New',
  'Studio Courier': 'Courier New',
}

const SERIF = /^(Georgia|Book Antiqua|Palatino Linotype|Constantia|Sylfaen|Cambria Math)$/
const MONO = /^(Consolas|Lucida Console)$/

/**
 * A font Word substitutes (not in the table) is measured as the generic
 * family it falls back to: serif as Times New Roman, monospace as Courier
 * New, everything else as Arial.
 */
function unitsFor(name) {
  const clean = String(name ?? '')
    .replace(/['"]/g, '')
    .trim()
  const known = FONT_UNITS[clean] ?? FONT_UNITS[SAME_METRICS[clean]]
  if (known) return known
  if (SERIF.test(clean)) return FONT_UNITS['Times New Roman']
  if (MONO.test(clean)) return FONT_UNITS['Courier New']
  return FONT_UNITS.Arial
}

const round6 = (value) => Math.round(value * 1e6) / 1e6

/**
 * A font's metrics as fractions of the em: { ascent, descent, lineGap, single }.
 * Empty input → null (the paragraph's or document's font cascades).
 */
export function fontMetrics(name) {
  if (!name || typeof name !== 'string' || !name.replace(/['"]/g, '').trim()) return null
  const [unitsPerEm, ascent, descent, lineGap] = unitsFor(name)
  return {
    ascent: round6(ascent / unitsPerEm),
    descent: round6(descent / unitsPerEm),
    lineGap: round6(lineGap / unitsPerEm),
    single: round6((ascent + descent + lineGap) / unitsPerEm),
  }
}

/** The custom properties studio.css lays a line out with, for a Word font name ('' when none). */
export function fontMetricsCss(name) {
  const metrics = fontMetrics(name)
  if (!metrics) return ''
  return `--studio-asc: ${metrics.ascent}; --studio-desc: ${metrics.descent}; --studio-gap: ${metrics.lineGap}`
}

/**
 * A w:spacing line rule → the custom properties studio.css turns into the
 * line height and the baseline offset ('' when the spacing sets no line):
 *
 *   --studio-line-factor × S × font size, at least --studio-line-min, is the height H;
 *   the baseline sits --studio-baseline-exact × H + --studio-baseline-top × (ascent + lineGap)
 *   + --studio-baseline-bottom × (H − descent) below the line top.
 *
 * @param {number|string|null} line twips (exact/atLeast) or 240ths of a single line (auto)
 * @param {string|null} lineRule 'auto' (also when absent), 'exact' or 'atLeast'
 */
export function lineSpacingCss(line, lineRule) {
  const value = Number(line)
  if (!Number.isFinite(value) || value <= 0) return ''
  const props = (factor, minPt, exact, top, bottom) =>
    `--studio-line-factor: ${factor}; --studio-line-min: ${minPt}pt; --studio-baseline-exact: ${exact}; --studio-baseline-top: ${top}; --studio-baseline-bottom: ${bottom}`

  if (lineRule === 'exact') return props(0, value / 20, 0.8, 0, 0)
  if (lineRule === 'atLeast') return props(1, value / 20, 0, 0, 1)
  const factor = round6(value / 240)
  return props(factor, 0, 0, Math.min(factor, 1), 0)
}

const SCRIPT_SCALE = 0.65

/**
 * Word's superscript and subscript (w:vertAlign), measured in Word.app 16.112
 * at 10–40 pt in Calibri, Times New Roman and Arial: the glyphs shrink to 0.65
 * of the run size (every font's OS/2 ySuperscriptYSize, 1331/2048), the
 * baseline moves up 0.35 or down 0.09 of the run size, and the line keeps its
 * height. On the canvas the run takes no line height and moves from the
 * shared baseline only by that much. '' for any other value.
 *
 * @param {string|null} vertAlign 'superscript' | 'subscript' | 'baseline'
 * @param {number|null} sizePt the run's own w:sz in points, when it has one
 */
export function vertAlignCss(vertAlign, sizePt = null) {
  const shift = { superscript: 0.35, subscript: -0.09 }[vertAlign]
  if (shift === undefined) return ''
  const fontSize = sizePt > 0 ? `${round6(sizePt * SCRIPT_SCALE)}pt` : `${SCRIPT_SCALE}em`
  return `font-size: ${fontSize}; line-height: 0; vertical-align: ${round6(shift / SCRIPT_SCALE)}em; top: 0`
}

/**
 * The paragraph mark's font (w:pPr/w:rPr): an empty paragraph is as tall as
 * its mark in Word. '' when the mark sets neither font nor size.
 *
 * @param {string|null} font the mark's w:rFonts ascii
 * @param {number|null} sizePt its w:sz in points
 */
export function paragraphMarkCss(font, sizePt) {
  const css = []
  const metrics = fontMetrics(font)
  if (metrics) css.push(`--studio-mark-asc: ${metrics.ascent}; --studio-mark-desc: ${metrics.descent}; --studio-mark-gap: ${metrics.lineGap}`)
  if (sizePt > 0) css.push(`--studio-mark-size: ${sizePt}pt`)
  return css.join('; ')
}

/**
 * A run font given only as a theme slot (w:asciiTheme minorHAnsi, majorAscii…):
 * the theme's typeface and metrics, which buildPresentationCss publishes as
 * --studio-font-minor / --studio-minor-asc… on the document. '' for other values.
 */
export function themeFontCss(asciiTheme) {
  const slot = /^minor/.test(asciiTheme ?? '') ? 'minor' : /^major/.test(asciiTheme ?? '') ? 'major' : null
  if (!slot) return ''
  return `font-family: var(--studio-font-${slot}); --studio-asc: var(--studio-${slot}-asc); --studio-desc: var(--studio-${slot}-desc); --studio-gap: var(--studio-${slot}-gap)`
}
